import { useId, useMemo, useRef, useState } from 'react';
import {
  DayPicker,
  type DateRange as DayPickerDateRange,
  type DayPickerProps,
  type Matcher,
} from '@daypicker/react';
import { PiCalendarBlank, PiCaretDown } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { DateInput } from 'src/modules/shared/components/atoms/inputs/date-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { Popover } from 'src/modules/shared/components/molecules/popovers/popover';
import { useDateRangeDraft } from 'src/modules/shared/hooks/use-date-range-draft';
import { useAnchoredOverlayLayout } from 'src/modules/shared/hooks/use-anchored-overlay-layout';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import type {
  CalendarDateRange,
  CalendarDateRangeBounds,
  CalendarDateRangePickerLabels,
  CalendarDateRangePreset,
  CalendarDateRangeSelection,
} from 'src/modules/shared/types/date-range.type';
import {
  CalendarDateRangeIssue,
  formatCalendarDateRange,
  parseCalendarDateToken,
  toCalendarDateToken,
} from 'src/modules/shared/utils/date-range.util';
import type { CalendarDateRangeIssueCode } from 'src/modules/shared/utils/date-range.util';
import {
  DATE_RANGE_PICKER_POPOVER_GAP,
  dateRangePickerClasses,
  getDateRangePickerDayPickerClassNames,
  getDateRangePickerPopoverContentClasses,
} from './date-range-picker.styles';
import '@daypicker/react/style.css';

type DayPickerLocale = DayPickerProps['locale'];

/** Props for the application-agnostic date-range picker. */
export interface DateRangePickerProps<TId extends string = string> {
  /** Currently applied concrete date range. */
  value: CalendarDateRange;
  /** Currently applied shortcut identifier. */
  selectedPresetId?: TId | null;
  /** Consumer-defined date shortcuts. */
  presets: CalendarDateRangePreset<TId>[];
  /** Consumer-provided visible and accessible copy. */
  labels: CalendarDateRangePickerLabels;
  /** Called only after a valid changed draft is explicitly applied. */
  onApply: (selection: CalendarDateRangeSelection<TId>) => void;
  /** Optional inclusive date limits. */
  bounds?: CalendarDateRangeBounds;
  /** Locale used by the calendar. */
  calendarLocale?: DayPickerLocale;
  /** BCP 47 locale used by the default trigger formatter. */
  locale?: string;
  /** Optional custom formatter for the applied trigger value. */
  formatValue?: (value: CalendarDateRange, presetId: TId | null) => string;
  /** Optional context shown below the manual inputs. */
  footerNote?: string;
  /** Optional externally computed error shown before draft validation errors. */
  errorMessage?: string | null;
  /** Whether the entire control is disabled. */
  disabled?: boolean;
  /** Additional class applied to the control wrapper. */
  className?: string;
  /** Additional class applied to the trigger. */
  triggerClassName?: string;
}

interface DateRangePanelProps<TId extends string> {
  titleId: string;
  draftValue: CalendarDateRange;
  draftPresetId: TId | null;
  presets: CalendarDateRangePreset<TId>[];
  labels: CalendarDateRangePickerLabels;
  bounds: CalendarDateRangeBounds;
  calendarLocale?: DayPickerLocale;
  numberOfMonths: number;
  errorMessage: string | null;
  footerNote?: string;
  canApply: boolean;
  maxHeight?: number;
  onDraftChange: (value: CalendarDateRange) => void;
  onPresetSelect: (preset: CalendarDateRangePreset<TId>) => void;
  onCancel: () => void;
  onApply: () => void;
}

const resolveIssueMessage = (
  issue: CalendarDateRangeIssueCode | null,
  labels: CalendarDateRangePickerLabels
): string | null => {
  switch (issue) {
    case null:
      return null;
    case CalendarDateRangeIssue.MISSING:
      return labels.missingError;
    case CalendarDateRangeIssue.INVALID:
      return labels.invalidError;
    case CalendarDateRangeIssue.ORDER:
      return labels.orderError;
    case CalendarDateRangeIssue.BEFORE_MIN:
      return labels.beforeMinError;
    case CalendarDateRangeIssue.AFTER_MAX:
      return labels.afterMaxError;
  }
};

const DateRangePanel = <TId extends string>({
  titleId,
  draftValue,
  draftPresetId,
  presets,
  labels,
  bounds,
  calendarLocale,
  numberOfMonths,
  errorMessage,
  footerNote,
  canApply,
  maxHeight,
  onDraftChange,
  onPresetSelect,
  onCancel,
  onApply,
}: DateRangePanelProps<TId>) => {
  const selected = useMemo<DayPickerDateRange | undefined>(() => {
    const from = parseCalendarDateToken(draftValue.from);
    const to = parseCalendarDateToken(draftValue.to);

    return from ? { from, to: to && to >= from ? to : undefined } : undefined;
  }, [draftValue.from, draftValue.to]);
  const disabledDays = useMemo<Matcher[]>(() => {
    const matchers: Matcher[] = [];
    const minDate = parseCalendarDateToken(bounds.minDate);
    const maxDate = parseCalendarDateToken(bounds.maxDate);

    if (minDate) {
      matchers.push({ before: minDate });
    }
    if (maxDate) {
      matchers.push({ after: maxDate });
    }

    return matchers;
  }, [bounds.maxDate, bounds.minDate]);
  const startMonth = parseCalendarDateToken(bounds.minDate) ?? undefined;
  const endMonth = parseCalendarDateToken(bounds.maxDate) ?? undefined;
  const isNarrowMobile = useMediaQuery('(max-width: 374px)');
  const dayPickerClassNames =
    getDateRangePickerDayPickerClassNames(isNarrowMobile);

  return (
    <div
      className={dateRangePickerClasses.panel}
      style={maxHeight == null ? undefined : { maxHeight }}
    >
      <div className={dateRangePickerClasses.panelBody}>
        <h2
          id={titleId}
          className="mb-3 hidden text-lg font-semibold text-primary-900 sm:block"
        >
          {labels.title}
        </h2>

        <fieldset className="mb-4">
          <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {labels.presets}
          </legend>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {presets.map((preset) => {
              const selectedPreset = draftPresetId === preset.id;

              return (
                <Button
                  key={preset.id}
                  type="button"
                  size="sm"
                  appearance={selectedPreset ? 'filled' : 'outline'}
                  variant={selectedPreset ? 'primary' : 'secondary'}
                  aria-pressed={selectedPreset}
                  className="min-h-10 min-w-0 justify-center whitespace-normal text-center"
                  onClick={() => onPresetSelect(preset)}
                >
                  {preset.label}
                </Button>
              );
            })}
          </div>
        </fieldset>

        <div className="flex justify-center overflow-x-auto border-y border-neutral-100 py-2">
          <DayPicker
            mode="range"
            selected={selected}
            onSelect={(range) =>
              onDraftChange({
                from: range?.from ? toCalendarDateToken(range.from) : undefined,
                to: range?.to ? toCalendarDateToken(range.to) : undefined,
              })
            }
            numberOfMonths={numberOfMonths}
            disabled={disabledDays}
            startMonth={startMonth}
            endMonth={endMonth}
            defaultMonth={selected?.from ?? endMonth}
            locale={calendarLocale}
            showOutsideDays
            excludeDisabled
            classNames={dayPickerClassNames}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block min-w-0">
            <span className="mb-1 block text-xs font-medium text-neutral-600">
              {labels.from}
            </span>
            <DateInput
              value={draftValue.from ?? ''}
              min={bounds.minDate}
              max={bounds.maxDate}
              aria-label={labels.from}
              containerClassName="w-full"
              onChange={(event) =>
                onDraftChange({ ...draftValue, from: event.target.value })
              }
            />
          </label>
          <label className="block min-w-0">
            <span className="mb-1 block text-xs font-medium text-neutral-600">
              {labels.to}
            </span>
            <DateInput
              value={draftValue.to ?? ''}
              min={bounds.minDate}
              max={bounds.maxDate}
              aria-label={labels.to}
              containerClassName="w-full"
              onChange={(event) =>
                onDraftChange({ ...draftValue, to: event.target.value })
              }
            />
          </label>
        </div>

        {footerNote ? (
          <Text size="tiny" className="mt-2 text-neutral-500">
            {footerNote}
          </Text>
        ) : null}
        {errorMessage ? (
          <p className="mt-2 text-sm text-danger-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </div>

      <div className="sticky bottom-0 flex flex-none justify-end gap-2 border-t border-neutral-100 bg-white px-4 py-3 sm:px-5">
        <Button
          type="button"
          appearance="ghost"
          variant="secondary"
          onClick={onCancel}
        >
          {labels.cancel}
        </Button>
        <Button type="button" disabled={!canApply} onClick={onApply}>
          {labels.apply}
        </Button>
      </div>
    </div>
  );
};

/**
 * Responsive date-range picker with draft/apply semantics.
 *
 * Phones receive a full-screen dialog, tablets a compact one-month popover,
 * and desktop screens a bounded two-month popover.
 */
export const DateRangePicker = <TId extends string>({
  value,
  selectedPresetId = null,
  presets,
  labels,
  onApply,
  bounds = {},
  calendarLocale,
  locale = 'en-US',
  formatValue,
  footerNote,
  errorMessage: externalErrorMessage = null,
  disabled = false,
  className = '',
  triggerClassName = '',
}: DateRangePickerProps<TId>) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isMobile = useMediaQuery('(max-width: 639px)');
  const showTwoMonths = useMediaQuery('(min-width: 1024px)');
  const titleId = useId();
  const overlayLayout = useAnchoredOverlayLayout({
    anchorRef: triggerRef,
    isOpen: isOpen && !isMobile,
    gap: DATE_RANGE_PICKER_POPOVER_GAP,
  });
  const draft = useDateRangeDraft({
    value,
    selectedPresetId,
    bounds,
    onApply,
  });
  const draftErrorMessage = draft.isDirty
    ? resolveIssueMessage(draft.issue, labels)
    : null;
  const errorMessage = externalErrorMessage ?? draftErrorMessage;
  const triggerValue = formatValue
    ? formatValue(value, selectedPresetId)
    : formatCalendarDateRange(value, locale);
  const closeAndRestoreFocus = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleClose = () => {
    draft.reset();
    closeAndRestoreFocus();
  };
  const handleApply = () => {
    if (draft.apply()) {
      closeAndRestoreFocus();
    }
  };
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      draft.reset();
      closeAndRestoreFocus();
      return;
    }
    setIsOpen(true);
  };

  const trigger = (
    <Button
      ref={triggerRef}
      type="button"
      appearance="outline"
      variant="secondary"
      disabled={disabled}
      aria-label={labels.trigger}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      className={`w-full min-w-0 justify-between gap-2 bg-white sm:w-auto ${triggerClassName}`}
      onClick={isMobile ? () => handleOpenChange(!isOpen) : undefined}
    >
      <span className="flex min-w-0 items-center gap-2">
        <PiCalendarBlank className="h-5 w-5 flex-none" aria-hidden="true" />
        <span className="truncate">{triggerValue || labels.title}</span>
      </span>
      <PiCaretDown className="h-4 w-4 flex-none" aria-hidden="true" />
    </Button>
  );
  const panel = (
    <DateRangePanel
      titleId={titleId}
      draftValue={draft.draftValue}
      draftPresetId={draft.draftPresetId}
      presets={presets}
      labels={labels}
      bounds={bounds}
      calendarLocale={calendarLocale}
      numberOfMonths={showTwoMonths ? 2 : 1}
      errorMessage={errorMessage}
      footerNote={footerNote}
      canApply={draft.canApply}
      maxHeight={isMobile ? undefined : overlayLayout.maxHeight ?? undefined}
      onDraftChange={draft.setDraftValue}
      onPresetSelect={draft.selectPreset}
      onCancel={handleClose}
      onApply={handleApply}
    />
  );

  if (isMobile) {
    return (
      <div className={`min-w-0 ${className}`}>
        {trigger}
        <Modal
          isOpen={isOpen}
          onClose={handleClose}
          ariaLabel={labels.title}
          closeLabel={labels.close}
          header={
            <Text as="h2" size="h6" weight="semibold">
              {labels.title}
            </Text>
          }
          bodyClassName="p-0"
          body={panel}
        />
      </div>
    );
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpenChange}
      handler={trigger}
      disabled={disabled}
      placement={overlayLayout.side === 'top' ? 'top-end' : 'bottom-end'}
      className={`min-w-0 ${className}`}
      contentClassName={getDateRangePickerPopoverContentClasses(showTwoMonths)}
    >
      <div role="dialog" aria-modal="false" aria-labelledby={titleId}>
        {panel}
      </div>
    </Popover>
  );
};
